import { getApiConfig } from '../config/apiConfig';
import { getTokenManager } from '../config/tokenManager';
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { ErrorFromResponse } from '../utils';
import { addLoggingInterceptors } from './logger';

export const userApiClient: AxiosInstance = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // handles arrays correctly for NestJS (no [] brackets)
  },
});

const BUSINESS_ID_EXCLUSIONS = [
  '/businesses',
  '/business/',
  '/users/',
  '/user/',
  '/subscription-plans/',
];

userApiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.baseURL = getApiConfig().apiBaseUrl;
    try {
      const businessId = getTokenManager().getBusinessId();
      if (
        businessId &&
        !BUSINESS_ID_EXCLUSIONS.some((segment) => config.url?.includes(segment))
      ) {
        if (!config.params) {
          config.params = {};
        }
        if (!config.params.businessId) {
          config.params.businessId = businessId;
        }
      }
      // First check for JWT access token (for email/password auth)
      const { accessToken } = getTokenManager().getUserTokens();
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        return config;
      }
    } catch (error) {
      getTokenManager().logger.error('Failed to get auth token:', { error });
    }

    return config;
  },
  (error) => {
    getTokenManager().logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
userApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data?.errorCode === 'AUTH.INVALID_REFRESH_TOKEN') {
      getTokenManager().removeUserTokens();
      getTokenManager().onAuthError();
      return Promise.reject(new ErrorFromResponse(response.data));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (
        originalRequest.url?.includes('/refresh') ||
        error.response?.data?.errorCode === 'AUTH.INVALID_REFRESH_TOKEN'
      ) {
        getTokenManager().removeUserTokens();
        if (!originalRequest.url?.includes('/auth/')) {
          getTokenManager().onAuthError();
        }
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        // Try to refresh JWT token first
        const { refreshToken } = getTokenManager().getUserTokens();
        if (refreshToken) {
          try {
            let deviceId: string | undefined;
            if (getTokenManager().getDeviceId) {
              deviceId = await getTokenManager().getDeviceId?.();
            }

            const { data: refreshResponse } = await userApiClient.post<any>(
              '/user/auth/refresh',
              { refreshToken, deviceId }
            );

            if (!refreshResponse.success) {
              throw new ErrorFromResponse(refreshResponse);
            }

            const {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            } = refreshResponse.data ?? {};

            if (!newAccessToken || !newRefreshToken) {
              throw new Error('Failed to get new tokens from refresh response');
            }

            getTokenManager().setUserTokens({
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            });

            // Update the authorization header with the new access token.
            userApiClient.defaults.headers.common['Authorization'] =
              `Bearer ${newAccessToken}`;
            return userApiClient(originalRequest); // Retry the original request with the new access token.
          } catch (refreshError) {
            getTokenManager().logger.error('Failed to refresh auth token:', {
              error: refreshError,
            });
            // JWT refresh failed, clear tokens
            getTokenManager().removeUserTokens();
          }
        }

        if (!originalRequest.url?.includes('/auth/')) {
          console.log('No valid auth method, redirecting to login');
          // No valid auth method, redirect to login
          getTokenManager().onAuthError();
        }
      } catch (refreshError) {
        getTokenManager().logger.error('Failed to refresh auth token:', {
          error: refreshError,
        });
        if (!originalRequest.url?.includes('/auth/')) {
          console.log('Failed to refresh auth token, redirecting to login');
          // Redirect to login or handle auth error
          getTokenManager().onAuthError();
        }
      }
    }

    // // Handle other errors
    // if (error.response?.status >= 500) {
    //   getTokenManager().logger.error('Server error:', error.response.data);
    // } else if (error.response?.status >= 400) {
    //   getTokenManager().logger.error('Client error:', error.response.data);
    // }

    return Promise.reject(error);
  }
);

addLoggingInterceptors(userApiClient, 'userApiClient');
